import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { TargetableInventoryList } from '@openx/types/targeting/inventoryAndContent.ts';
import { useIsAllowed } from '@openx/utils/permission/useIsAllowed.ts';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { searchInventories } from '@openx/utils/state/packages/inventories/api.ts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getExtendQuery } from '../../../Targeting/shared/InventoryAndContent/utils.ts';
import { useTargetingContext } from '../../context/TargetingContext.tsx';

interface UseFetchInventoriesBySearchTextProps {
  accountId: string;
  searchPhrase: string;
  includes: TargetableInventoryList;
  instanceUid?: string;
}

export const useFetchInventoriesBySearchText = ({
  accountId,
  includes,
  instanceUid,
  searchPhrase,
}: UseFetchInventoriesBySearchTextProps) => {
  const { t } = useTranslation();
  const { isAllowed } = useIsAllowed();

  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();

  const { isLoading, data, refetch } = useQuery<TargetableInventoryList>({
    enabled: !!searchPhrase,
    meta: {
      errorMessage: t('Failed to fetch Inventories'),
    },
    queryFn: () => {
      const isUserAllowed =
        isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE)) ||
        isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));

      return searchInventories(
        isUserAllowed && isBefInstance,
        {},
        instanceUid,
        accountId,
        false,
        searchPhrase,
        getExtendQuery(includes)
      );
    },
    queryKey: [instanceUid, searchPhrase, includes, isBefInstance, accountId],
  });

  return {
    data,
    isLoading,
    refetch,
  };
};
